/**
 * The organisation's property definitions, as TanStack Query hooks, beside
 * crm.ts and people.ts.
 *
 * A definition says what a key means: its label, type, options and group.
 * Values live on the record (a company's, deal's or person's `properties`)
 * or, for leads, in the custom data exactly as it arrived; definitions are
 * read once and shared by the record pages, the lists, the filter panel,
 * the import mapping, routing filters and the search bar.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap } from './client';
import type { components } from './schema';

type S = components['schemas'];
export type PropertyDef = S['PropertyDef'];
export type PropertyObject = S['PropertyObject'];
export type PropertyType = S['PropertyType'];
export type PropertyOption = S['PropertyOption'];
export type SubmissionProperty = S['SubmissionProperty'];
export type SeedPropertiesResponse = S['SeedPropertiesResponse'];
export type CreatePropertyBody = S['CreatePropertyBody'];
export type UpdatePropertyBody = S['UpdatePropertyBody'];

/**
 * One object's definitions (or every object's), in order. Archived ones only
 * when asked. Cached for a minute: they change rarely, and every write here
 * invalidates them.
 */
export function useProperties(
  objectType: PropertyObject | null,
  { enabled = true, archived = false }: { enabled?: boolean; archived?: boolean } = {},
) {
  return useQuery({
    queryKey: ['properties', objectType ?? 'all', archived],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/properties', {
          params: {
            query: {
              ...(objectType ? { object_type: objectType } : {}),
              ...(archived ? { include_archived: true } : {}),
            },
          },
        }),
        'PropertyListResponse',
      ),
    staleTime: 60 * 1000,
    enabled,
  });
}

/** Every definition write, each invalidating the definitions. */
export function usePropertyEdits() {
  const qc = useQueryClient();
  const done = () => {
    qc.invalidateQueries({ queryKey: ['properties'] });
    // The lead page types its values from the definitions.
    qc.invalidateQueries({ queryKey: ['lead'] });
  };
  const create = useMutation({
    mutationFn: async (body: CreatePropertyBody) =>
      unwrap(await api.POST('/api/v1/properties', { body }), 'PropertyDef'),
    onSuccess: done,
  });
  const update = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: UpdatePropertyBody }) =>
      unwrap(await api.PATCH('/api/v1/properties/{id}', { params: { path: { id } }, body }), 'PropertyDef'),
    onSuccess: done,
  });
  const archive = useMutation({
    mutationFn: async ({ id, archived }: { id: string; archived: boolean }) =>
      archived
        ? unwrap(await api.POST('/api/v1/properties/{id}/archive', { params: { path: { id } } }), 'PropertyDef')
        : unwrap(await api.POST('/api/v1/properties/{id}/unarchive', { params: { path: { id } } }), 'PropertyDef'),
    onSuccess: done,
  });
  const remove = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.DELETE('/api/v1/properties/{id}', { params: { path: { id } } });
      if (res.error !== undefined) {
        const e = res.error as { detail?: string };
        throw new Error(e.detail ?? 'Delete failed');
      }
      return id;
    },
    onSuccess: done,
  });
  const reorder = useMutation({
    mutationFn: async ({ objectType, ids }: { objectType: PropertyObject; ids: string[] }) =>
      unwrap(
        await api.PUT('/api/v1/properties/positions', { body: { object_type: objectType, ids } }),
        'PropertyListResponse',
      ),
    onSuccess: done,
  });
  const seed = useMutation({
    mutationFn: async (dryRun: boolean) =>
      unwrap(
        await api.POST('/api/v1/properties/seed-from-campaigns', { body: { dry_run: dryRun } }),
        'SeedPropertiesResponse',
      ),
    onSuccess: (r) => {
      if (!r.dry_run) done();
    },
  });
  return { create, update, archive, remove, reorder, seed };
}

/** Change a stored person's properties: keys sent are set, null removes. */
export function useUpdatePersonProperties() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, properties }: { id: string; properties: Record<string, unknown> }) =>
      unwrap(
        await api.PATCH('/api/v1/people/{id}', { params: { path: { id } }, body: { properties } }),
        'PersonPropertiesView',
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['person'] });
      qc.invalidateQueries({ queryKey: ['people'] });
    },
  });
}
